import { useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour.css';
import { useSettingsStore } from '../../store/useSettingsStore';

/**
 * Main onboarding tour controller component using driver.js
 * Shows a guided tour for new users to understand the app
 */
export function OnboardingTour() {
    const hasSeenTour = useSettingsStore((state) => state.hasSeenTour);
    const setHasSeenTour = useSettingsStore((state) => state.setHasSeenTour);
    const setIsTourActive = useSettingsStore((state) => state.setIsTourActive);
    const driverRef = useRef<ReturnType<typeof driver> | null>(null);

    useEffect(() => {
        // Only run for users who haven't seen the tour
        if (hasSeenTour) return;

        // Small delay to ensure all components are mounted
        const timer = setTimeout(() => {
            // Disable auto-hide UI while tour is active
            setIsTourActive(true);

            const driverInstance = driver({
                showProgress: true,
                showButtons: ['next', 'previous', 'close'],
                allowClose: true,
                overlayColor: 'rgba(0, 0, 0, 0.6)',
                stagePadding: 10,
                stageRadius: 16,
                popoverClass: 'tour-popover',
                progressText: 'Step {{current}} of {{total}}',
                nextBtnText: 'Next →',
                prevBtnText: '← Back',
                doneBtnText: "🎉 Let's Go!",
                onCloseClick: () => {
                    setHasSeenTour(true);
                    setIsTourActive(false);
                    driverInstance.destroy();
                },
                onDestroyStarted: () => {
                    setHasSeenTour(true);
                    setIsTourActive(false);
                },
                steps: [
                    {
                        popover: {
                            title: '👋 Welcome to Study Saga!',
                            description: "Let us show you around so you can make the most of your focus sessions.",
                            side: 'over',
                            align: 'center',
                        },
                    },
                    {
                        element: '[data-tour="timer-section"]',
                        popover: {
                            title: '⏱️ Pomodoro Timer',
                            description: 'This is your Pomodoro Timer. Focus, then take a break. Press Space to start/pause, or R to reset! Customize your timer durations in settings.',
                            side: 'bottom',
                            align: 'center',
                        },
                    },
                    {
                        element: '[data-tour="music-player"]',
                        popover: {
                            title: '🎵 Music Player',
                            description: 'Enjoy music while you study! Choose your genre in settings - lo-fi, synthwave, and more. Use the controls to play, pause, or skip tracks.',
                            side: 'top',
                            align: 'center',
                        },
                    },
                    {
                        element: '[data-tour="xp-section"]',
                        popover: {
                            title: '⭐ XP & Levels',
                            description: 'Earn XP by completing Pomodoros! Level up and grow your rank. The more you study, the more you progress!',
                            side: 'right',
                            align: 'start',
                        },
                    },
                    {
                        element: '[data-tour="chat-section"]',
                        popover: {
                            title: '💬 Global Chat',
                            description: 'Connect with other students worldwide! Share motivation, ask questions, or just say hi. Click to expand the chat.',
                            side: 'left',
                            align: 'center',
                        },
                    },
                    {
                        element: '[data-tour="presence-counter"]',
                        popover: {
                            title: '👥 Online Users',
                            description: 'See how many people are studying with you right now. You\'re never alone on your focus journey!',
                            side: 'left',
                            align: 'start',
                        },
                    },
                    {
                        element: '[data-tour="settings-button"]',
                        popover: {
                            title: '⚙️ Settings',
                            description: 'Customize your experience! Adjust timer durations, change backgrounds, manage sounds, and more.',
                            side: 'left',
                            align: 'start',
                        },
                    },
                    {
                        popover: {
                            title: '🚀 You\'re All Set!',
                            description: 'Start your first Pomodoro and begin your productivity journey. Good luck with your studies!',
                            side: 'over',
                            align: 'center',
                        },
                    },
                ],
            });

            driverRef.current = driverInstance;
            driverInstance.drive();
        }, 1000);

        return () => {
            clearTimeout(timer);
            if (driverRef.current) {
                driverRef.current.destroy();
            }
        };
    }, [hasSeenTour, setHasSeenTour]);

    return null;
}
